import { getServices } from '../../infra/startup';

import authV2 from '../v2/auth';
import initializeABTestingInterface from './ABTesting';
import { initializeAccessControlInterfaceV1 } from './accessControl';
import initializeAnalyticsInterface from './analytics';
import initializeAppInterfaceV1 from './app';
import initializeAuthProviderInterface from './auth';
import initializeBreadcrumbInterfaceV1 from './breadcrumb';
import { initializeCriterionInterfaceV1 } from './Criterion/singleton';
import initializeEventsInterfaceV1 from './events';
import initializeFallbackInterface from './Fallback';
import initializeFeatureFlagInterfaceV1 from './featureFlags';
import initializeGrantsInterfaceV1 from './grants';
import initializeGrantsHistoryInterfaceV1 from './grantsHistory';
import initializeGraphQLInterfaceV1 from './graphql';
import initializeLocalizationInterface from './localization';
import initializeMFERouterInterface from './mfeRouter';
import initializeMonitoringInterfaceV1 from './monitoring';
import initializeNavigationInterface from './navigation';
import initializeLoggerInterface from './clientOsLogger';
import optimizely from './optimizely';
import OrgSelector from './orgSelector';
import initializeRoutesInterface from './routes';
import initializeServiceRoutingInterfaceV1 from './serviceRouting';
import initializeServiceWorkerInterface from './serviceWorker';
import initializeSessionInterfaceV1 from './session';
import initializeStoreInterfaceV1 from './store';
import initializeTenantHandlerInterfaceSingleton from './TenantHandler';
import initializeThemeInterface from './theme';
import { UserSessionInterfaceType } from './types';
import { initializeUserActivityInterface } from './userActivity';
import initializeUserInterfaceV1 from './userInterface';
import {
  StorageLocale,
  localeSessionRepository
} from '../../behaviors/afterInitBehaviors/storageLocale';
import InitializeDeprecatedServices from '../../behaviors/afterInitBehaviors/initializeDeprecatedServices';
import { container } from 'tsyringe';
import type { APIv1Type } from '@clientos/types';
import AuthenticationProviderEnum from '../../config/authenticationProviderEnum';
import { initializeEntitlementsInterfaceV1 } from './entitlements';
import { bootstrapManager } from '../../infra/startup/bootstrapManager';
import {
  AuthProvidersPropsType,
  CommonsInitializeType
} from '../../infra/startup/types';

export default async function initializeShellInterfaceV1(
  commonsInitializeInterfaces: CommonsInitializeType
): Promise<APIv1Type> {
  const services = getServices();
  const {
    authTokenService,
    mfeRouterService,
    sessionService,
    loginService,
    tenantHandlerService,
    supportSessionService,
    userService,
    localizationService,
    navigationService
  } = services;

  const {
    accessControl: accessControlInput,
    adapter: adapterInput,
    analytics: analyticsInput,
    localization: localizationInput,
    login: loginInput,
    navigation: navigationInput,
    onboarding: onboardingInput,
    userOnboarding: userOnboardingInput,
    optimizely: optimizelyInput,
    stack,
    basePath
  } = commonsInitializeInterfaces;

  /** Integration phase
   * - Cross-service integration
   */
  bootstrapManager.setIntegration();

  //Initializing auth provider
  const authProvider = await initializeAuthProviderInterface();

  const appInterface = await initializeAppInterfaceV1();

  const userActivityInterface = await initializeUserActivityInterface();

  const abTestingInterface = await initializeABTestingInterface();

  const analyticsInterface = await initializeAnalyticsInterface({
    isEnabled: analyticsInput.enabled
  });

  const navigationInterface = await initializeNavigationInterface({
    useLegacyNavigation: navigationInput.useLegacyNavigation
  });

  const localizationInterface = await initializeLocalizationInterface();

  const accessControlInterface = await initializeAccessControlInterfaceV1({
    accessControl: accessControlInput
  });

  const featureFlagsInterface = await initializeFeatureFlagInterfaceV1();

  const newLoggerInterface = await initializeLoggerInterface();

  const grantsInterface = await initializeGrantsInterfaceV1();

  const grantsHistoryInterface = await initializeGrantsHistoryInterfaceV1();

  const userInterface = await initializeUserInterfaceV1();

  const sessionInterface = await initializeSessionInterfaceV1();

  const graphQLInterface = await initializeGraphQLInterfaceV1();

  const monitoringInterface = await initializeMonitoringInterfaceV1();

  const themeInterface = await initializeThemeInterface();

  const eventsInterface = await initializeEventsInterfaceV1();

  const storeInterface = await initializeStoreInterfaceV1();

  const entitlementsInterface = await initializeEntitlementsInterfaceV1();

  const criterionInterface = await initializeCriterionInterfaceV1();

  const serviceRouting = await initializeServiceRoutingInterfaceV1({
    onboardingInput
  });

  const fallbackInterface = await initializeFallbackInterface();

  const serviceWorkerInterface = await initializeServiceWorkerInterface();

  const routesInterface = await initializeRoutesInterface();

  const tenantHandlerInterface =
    await initializeTenantHandlerInterfaceSingleton();

  const breadcrumbsInterface = await initializeBreadcrumbInterfaceV1();

  // Deprecated API
  // My desire is to remove that, but unfortunately is been used in others MFEs.
  const userSession: UserSessionInterfaceType = {
    redirectToLogin: loginService.redirectToLogin,
    getLoginPath: loginService.getLoginPath
  };

  /**
   * Below, we have some pattern issues.
   *
   */
  bootstrapManager.setNonCanonicalInitialization();

  // Deprecated
  optimizely(optimizelyInput);

  // Initialize deprecated auth v2
  const authProviderV2 = authV2(loginInput);
  authProviderV2._setSessionService(sessionService);
  authProviderV2._setTenantHandlerService(tenantHandlerService);
  authProviderV2._setLoginService(loginService);

  const orgSelector = await OrgSelector({
    onboarding: onboardingInput,
    authProvider: authProviderV2.createOrgedAuthProvider(),
    orglessAuthProvider: authProviderV2.createOrglessAuthProvider(),
    navigation: navigationInterface,
    localization: localizationInterface,
    sessionService,
    tenantHandlerService,
    userService
  });

  /**
   * Starting MFE Router below
   */
  // TODO we'll remove all this code when interfaces won't be necessary anymore.
  mfeRouterService.setInterfaceDependencies({
    interfaces: {
      store: getServices().storeService,
      navigation: navigationInterface,
      fallback: fallbackInterface,
      criterion: getServices().criterionService.getCriterionInstance(),
      authProvider,
      localization: localizationInterface
    }
  });

  const isNative: boolean = container.resolve('IsNative');

  const authProviderProps: AuthProvidersPropsType =
    container.resolve('AuthProviderProps');
  /***
   * TODO: Workaround to fix login loop on /activate.
   * Need to refactor since this is an afterInitBehavior.
   */
  if (
    authProviderProps?.authenticationProvider ===
    AuthenticationProviderEnum?.authz
  )
    await InitializeDeprecatedServices?.init?.({
      sessionService,
      orgSelector,
      isNative
    });

  // TODO: his should be a beforeInitBehavior
  // Initialize StorageLocale before router initialization to ensure locale is preserved after login.
  !isNative &&
    (await StorageLocale?.init?.({
      navigationService,
      localizationService,
      localeSessionRepository
    }));

  // TODO: merge Start and Init.
  if (!adapterInput?.disableRouter) {
    await mfeRouterService.init({
      localization: localizationInput,
      onboarding: onboardingInput,
      login: loginInput,
      userOnboarding: userOnboardingInput,
      basePath: basePath,
      stack: stack
    });
    await mfeRouterService.start();
  }
  /**
   * Bootstrapping mfeRouter  finished.
   */

  const microfrontendRouter = await initializeMFERouterInterface({
    isEnabled: !adapterInput?.disableRouter
  });

  return {
    app: appInterface,
    abTesting: abTestingInterface,
    userInterface,
    sessionInterface,
    sessionService,
    authProvider,
    analytics: analyticsInterface,
    events: eventsInterface,
    navigation: navigationInterface,
    store: storeInterface,
    featureFlags: featureFlagsInterface,
    localization: localizationInterface,
    orgSelector,
    theme: themeInterface,
    serviceRouting,
    logger: newLoggerInterface,
    userActivity: userActivityInterface,
    entitlements: entitlementsInterface,
    accessControl: accessControlInterface,
    criterion: criterionInterface as any,
    userSession,
    fallback: fallbackInterface,
    microfrontendRouter,
    tenantHandlerInterface,
    tenantHandler: tenantHandlerService,
    authToken: authTokenService,
    breadcrumbs: breadcrumbsInterface,
    routes: routesInterface,
    supportSessionService,
    monitoring: monitoringInterface,
    serviceWorker: serviceWorkerInterface,
    userService,
    grants: grantsInterface,
    grantsHistory: grantsHistoryInterface,
    graphql: graphQLInterface
  };
}
